import { useEffect, useState, useRef } from "react";
import {
  Box,
  Flex,
  useToast,
  Button,
  FormControl,
  FormLabel,
  Input,
  useColorModeValue,
  Center,
  VStack,
  Text,
  Stack,
  SimpleGrid,
  Spacer,
  HStack,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Link,
} from "@chakra-ui/react";

import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import { Trans } from "@lingui/react/macro";
import { useNavigate } from "react-router-dom";
import { Marker } from "react-map-gl";

import Map, {
  Source,
  GeolocateControl,
  FullscreenControl,
  NavigationControl,
  ScaleControl,
} from "react-map-gl";
import type { ViewState } from "react-map-gl";
import AddViewQrReader from "./AddViewQrReader";
import AddViewName from "./AddViewName";
import { IDevice } from "../../types/deviceInterface";

const AddView = () => {
  const toast = useToast();
  const api = useAxios();
  const navigate = useNavigate();
  const [isQRModalOpen, setQRModalOpen] = useState(false);
  const [deviceAddress, setDeviceAddress] = useState("");
  const mapRef = useRef<any>(null);
  const geolocateControlRef = useRef<any>(null);

  const [step, setStep] = useState<1 | 2>(1);
  const [accuracy, setAccuracy] = useState<number>(0);
  const [altitudeAccuracy, setAltitudeAccuracy] = useState<number | null>(null);
  const [locationIsLoading, setLocationIsLoading] = useState(false);
  const [altitude, setAltitude] = useState<number | null>(null);
  const [deviceDescription, setDeviceDescription] = useState<string>("");

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: <Trans>Geolocation not supported</Trans>, status: "error" });
      return;
    }
    setLocationIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
        setAccuracy(pos.coords.accuracy);
        setAltitude(pos.coords.altitude ?? 0);
        setAltitudeAccuracy(pos.coords.altitudeAccuracy);
        setLocationIsLoading(false);
      },
      () => {
        toast({ title: <Trans>Unable to retrieve your location</Trans>, status: "error" });
        setLocationIsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const [deviceName, setDeviceName] = useState("");
  const [sensorDepth, setSensorDepth] = useState<number>();
  const [flowSensorSize, setFlowSensorSize] = useState<number>();
  const [deviceType, setDeviceType] = useState<string>("");
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [devices, setDevices] = useState<IDevice[]>([]);

  const openQRModal = () => {
    setQRModalOpen(true);
    setStep(1);
  };
  const closeQRModal = () => {
    setQRModalOpen(false);
    setStep(1);
  };

  const handleMapClick = (evt: any) => {
    const { lng, lat } = evt.lngLat;
    setLocation({ latitude: lat, longitude: lng });

    if (mapRef.current) {
      setAltitude(mapRef.current.queryTerrainElevation(evt.lngLat));
      console.log("Altitude:", altitude);
    }
  };

  const fetchDevices = async () => {
    try {
      const res = await api.get("/frontend/devices/connection-quality/");
      setDevices(res.data);
    } catch (err: any) {
      toast({
        title: <Trans>Error fetching devices</Trans>,
        description: err.response?.data?.detail || err.message,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleApply = async () => {
    if (!location) {
      return toast({ title: <Trans>Invalid location</Trans>, status: "error" });
    }
    const payload: any = {
      label_code: deviceAddress.trim(),
      latitude: location.latitude,
      longitude: location.longitude,
      accuracy: accuracy,
      altitude: altitude,
      altitude_accuracy: altitudeAccuracy,
    };

    payload.device_name = deviceName.trim();
    payload.device_description = deviceDescription.trim();
    if (deviceType === "SENSOR") {
      payload.sensor_depth = sensorDepth;
    }
    if (deviceType === "VALVE_WITH_FLOW_SENSOR") {
      payload.flow_sensor_size = flowSensorSize;
    }

    try {
      const res = await api.post("/frontend/devices/custom-associate/", payload);
      toast({
        title:
          res.status === 201 ? (
            <Trans>Device associated successfully</Trans>
          ) : (
            <Trans>Device updated successfully</Trans>
          ),
        status: "success",
      });
      closeQRModal();
      setDeviceAddress("");
      fetchDevices();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.detail || "Unknown error";
      toast({ title: msg, status: "error" });
    } finally {
      setDeviceName("");
      setSensorDepth(undefined);
      setFlowSensorSize(undefined);
      setDeviceType("");
      setLocation(null);
    }
  };

  const handleScanned = async (scannedAddress: string) => {
    try {
      setDeviceAddress(scannedAddress);

      const resp = await api.post("/frontend/devices/type/", {
        label_code: scannedAddress,
      });
      const typeRaw: string = resp.data.device_type;

      console.log("Device type:", typeRaw);

      setDeviceType(typeRaw.toUpperCase());

      setStep(2);
    } catch (err: any) {
      const msg = err.response?.data?.error || "Unknown error";
      toast({ title: msg, status: "error" });
    }
  };

  const getDeviceColor = (device: IDevice) => {
    console.log("Device last seen:", device.last_seen);
    if (
      device.last_seen &&
      Date.now() - new Date(device.last_seen).getTime() > 2 * 60 * 60 * 1000
    ) {
      return "gray.500";
    }

    if (device.tot_rssi && device.tot_rssi < -90) {
      return "red.500";
    }
    if (device.tot_rssi && device.tot_rssi < -84) {
      return "yellow.500";
    }
    if (device.tot_rssi && device.tot_rssi >= -84) {
      return "green.500";
    }

    // Gray if last_seen timestamp is older than modified_at (device updated after last seen)
    if (
      (device as any).last_seen &&
      (device as any).modified_at &&
      new Date((device as any).last_seen).getTime() <
        new Date((device as any).modified_at).getTime()
    ) {
      return "gray.600";
    }

    return "teal.800";
  };

  return (
    <>
      <Navbar>
        <Box maxW="1000px" mx="auto">
          <Center>
            <Box
              width="350px"
              border="1px solid #ccc"
              borderRadius="20px"
              overflow="hidden"
              boxShadow="md"
              position="relative"
              height={{ base: "500px", lg: "700px" }}
            >
              <Map
                mapLib={import("mapbox-gl")}
                mapStyle="mapbox://styles/mapbox/satellite-v9"
                mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                style={{ width: "100%", height: "100%" }}
                onClick={handleMapClick}
                onLoad={() => geolocateControlRef.current?.trigger()}
              >
                <Source
                  id="mapbox-dem"
                  type="raster-dem"
                  url="mapbox://mapbox.mapbox-terrain-dem-v1"
                  tileSize={512}
                  maxzoom={14}
                />
                <GeolocateControl
                  ref={geolocateControlRef}
                  positionOptions={{ enableHighAccuracy: true }}
                  trackUserLocation={true}
                  showUserLocation={true}
                />
                <FullscreenControl />
                <NavigationControl />

                {location && (
                  <Marker
                    longitude={location.longitude}
                    latitude={location.latitude}
                    anchor="bottom"
                  >
                    <Box
                      w="20px"
                      h="20px"
                      bg="teal.500"
                      borderRadius="full"
                      border="2px solid white"
                    />
                  </Marker>
                )}
                {devices.map(
                  (device) =>
                    device.latitude &&
                    device.longitude && (
                      <Marker
                        key={device.device_id}
                        longitude={device.longitude}
                        latitude={device.latitude}
                        anchor="center"
                      >
                        <Box position="relative" w="18px" h="18px">
                          {device.tot_hops !== undefined && (
                            <Text
                              position="absolute"
                              top="-16px"
                              left="50%"
                              transform="translateX(-50%)"
                              fontSize="sm"
                              fontWeight="semibold"
                              pointerEvents="none"
                              mb={1}
                              color="white"
                              textShadow={`
                                -1px -1px 0 #000,
                                 1px -1px 0 #000,
                                -1px  1px 0 #000,
                                 1px  1px 0 #000
                              `}
                              lineHeight="1"
                            >
                              {device.tot_hops}
                            </Text>
                          )}
                          <Box
                            w="18px"
                            h="18px"
                            bg={getDeviceColor(device)}
                            borderRadius="full"
                            border="2px solid white"
                          />
                        </Box>
                      </Marker>
                    )
                )}

                <Box position="absolute" top="10px" left="10px">
                  <Link
                    color="teal.500"
                    fontWeight="bold"
                    onClick={() => navigate("/add-device/manual")}
                  >
                    <Trans>Manual Add</Trans>
                  </Link>
                </Box>
              </Map>
              <Box position="absolute" bottom="0" width="100%" py="4" textAlign="center">
                <Flex justifyContent="center" p={3}>
                  <Button colorScheme="teal" onClick={openQRModal}>
                    <Trans>Add a Device</Trans>
                  </Button>
                </Flex>
              </Box>
            </Box>
          </Center>
        </Box>
      </Navbar>
      <Modal
        isOpen={isQRModalOpen}
        onClose={() => {
          closeQRModal();
          setStep(1);
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            {step === 1 && (
              <AddViewQrReader
                deviceAddress={deviceAddress}
                setDeviceAddress={setDeviceAddress}
                onScanned={(code: string) => handleScanned(code)}
                onCancel={() => {
                  closeQRModal();
                  setStep(1);
                }}
              />
            )}
            {step === 2 && (
              <AddViewName
                deviceType={deviceType}
                deviceName={deviceName}
                setDeviceName={setDeviceName}
                sensorDepth={sensorDepth}
                setSensorDepth={setSensorDepth}
                flowSensorSize={flowSensorSize}
                setFlowSensorSize={setFlowSensorSize}
                location={location}
                registerLoading={locationIsLoading}
                onRegisterLocation={getLocation}
                onBack={() => setStep(1)}
                onApply={handleApply}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddView;
